import { useState } from "react";
import { ZoomIn, Download, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";

const CATALOG_IMG = "/images/catalogo-accesorios.jpeg";

const STATS = [
  { value: "500+", label: "Clientes Atendidos" },
  { value: "100%", label: "Satisfacción Garantizada" },
  { value: "Rápido", label: "Servicio Inmediato" },
  { value: "Casa 93", label: "Entrada UCC, Altamira" },
];

export default function AboutSection() {
  const [open, setOpen] = useState(false);

  return (
    <section id="nosotros" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* Left: Catálogo de Accesorios */}
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-[#FFE4E8] rounded-3xl -z-10" />

            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="catalog-image-trigger"
              aria-label="Ampliar catálogo de accesorios"
              className="group relative block w-full rounded-3xl overflow-hidden shadow-xl border border-gray-100 bg-white cursor-zoom-in focus:outline-none focus:ring-4 focus:ring-[#E65B78]/30"
            >
              <img
                src={CATALOG_IMG}
                alt="Catálogo de accesorios Printacce"
                data-testid="catalog-image"
                className="w-full h-auto object-contain transition-transform duration-500 group-hover:scale-[1.02]"
              />
              {/* Hover overlay + zoom hint */}
              <div className="absolute inset-0 bg-[#111111]/0 group-hover:bg-[#111111]/10 transition-colors duration-300" />
              <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur-sm text-[#E65B78] text-xs font-bold px-3 py-2 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <ZoomIn size={14} />
                Ampliar
              </span>
            </button>

            {/* Action buttons */}
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="catalog-zoom-btn"
                className="inline-flex items-center gap-2 bg-[#E65B78] hover:bg-[#D44B66] text-white text-sm font-bold px-5 py-2.5 rounded-full transition-all duration-200 shadow-md"
              >
                <ZoomIn size={16} />
                Ver en grande
              </button>
              <a
                href={CATALOG_IMG}
                download="Catalogo-Printacce.jpeg"
                data-testid="catalog-download-btn"
                className="inline-flex items-center gap-2 border-2 border-[#E65B78] text-[#E65B78] hover:bg-[#FFE4E8] text-sm font-bold px-5 py-2.5 rounded-full transition-all duration-200"
              >
                <Download size={16} />
                Descargar catálogo
              </a>
            </div>
          </div>

          {/* Right: Content (sin cambios de texto) */}
          <div className="lg:pl-4 mt-8 lg:mt-0">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E65B78] mb-3">
              Sobre Nosotros
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#111111] mb-6">
              Tu Aliado de Confianza en Managua
            </h2>
            <div className="space-y-4 text-[#5C5C5C] text-base leading-relaxed">
              <p>
                Somos <strong className="text-[#111111]">Printacce</strong>, un negocio local
                nacido con el objetivo de ser el centro de servicios de impresión más cercano
                y confiable para los estudiantes y profesionales de Altamira, Managua.
              </p>
              <p>
                Nos ubicamos estratégicamente en la <strong className="text-[#111111]">Entrada
                UCC, 1/2 cuadra arriba y 1/2 abajo, casa 93, Altamira, Managua</strong>, para estar siempre
                a la mano cuando más nos necesitas.
              </p>
              <p>
                Más que un servicio de copias, somos tu solución integral: imprimimos tus
                documentos, digitalizamos tus archivos, te ayudamos con tu
                <strong className="text-[#111111]"> Visa Americana</strong> y te vendemos
                los accesorios electrónicos que necesitas.
              </p>
            </div>

            {/* Stats */}
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {STATS.map(({ value, label }) => (
                <div key={label} className="text-center sm:text-left">
                  <p className="text-2xl font-black text-[#E65B78] leading-none">{value}</p>
                  <p className="text-xs text-[#5C5C5C] font-medium leading-tight mt-1">{label}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href="#contacto"
                data-testid="about-contact-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#E65B78] hover:bg-[#D44B66] text-white font-bold px-6 py-3 rounded-full transition-all duration-200 shadow-md"
              >
                Visítanos
              </a>
              <a
                href="https://wa.me/50589984547"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#E65B78] text-[#E65B78] hover:bg-[#FFE4E8] font-bold px-6 py-3 rounded-full transition-all duration-200"
              >
                Escribir al WhatsApp
              </a>
            </div>
          </div>

        </div>
      </div>

      {/* Lightbox / Zoom modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="catalog-modal"
          className="max-w-3xl w-[95vw] max-h-[92vh] overflow-y-auto p-2 sm:p-4 bg-white border-none"
        >
          <DialogTitle className="sr-only">Catálogo de accesorios Printacce</DialogTitle>
          <DialogClose
            data-testid="catalog-modal-close"
            className="absolute right-3 top-3 z-10 rounded-full bg-white/90 backdrop-blur-sm p-2 shadow-md text-[#111111] hover:bg-white transition"
          >
            <X size={18} />
          </DialogClose>
          <img
            src={CATALOG_IMG}
            alt="Catálogo de accesorios Printacce ampliado"
            className="w-full h-auto object-contain rounded-2xl"
          />
          <div className="flex justify-center py-3">
            <a
              href={CATALOG_IMG}
              download="Catalogo-Printacce.jpeg"
              data-testid="catalog-modal-download-btn"
              className="inline-flex items-center gap-2 bg-[#E65B78] hover:bg-[#D44B66] text-white text-sm font-bold px-6 py-3 rounded-full transition-all duration-200 shadow-md"
            >
              <Download size={16} />
              Descargar catálogo
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
